// @ts-nocheck
import Editor from './../../../../../components/Editor/Editor';
import {
  FileText,
  MapPin,
  Phone,
  ArrowSquareOut,
  Clock,
  ForkKnife
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
  formatDurationDisplay,
  formatTimeLocale
} from '../../../../../utils/dateUtility';
import { useTimeFormat } from '../../../../../hooks/useTimeFormat';
import { openMaps } from '../../../helpers/contact';
import * as PropTypes from 'prop-types';

/**
 * A service card component for activities.
 *
 * @param {Object} servicefields - the fields of the service
 * @param {string} tripdetaileddayserviceid - the id of the tripdetaileddayservice
 * @param {boolean} isPreview - whether the service card is in preview mode
 * @param {boolean} isDesktop - whether the device is desktop
 * @returns {JSX.Element}
 */
const ActivityCard = ({
  servicefields,
  tripdetaileddayserviceid,
  isPreview,
  isDesktop
}) => {
  const { t } = useTranslation(['services']);
  const { timeFormat } = useTimeFormat();

  //Preview Content
  if (isPreview) {
    return (
      <div>
        <div className='activity-card__header'>
          <h3 className='activity-card__title'>{servicefields?.TITLE || ''}</h3>
        </div>

        {servicefields?.STARTTIME && (
          <div className='activity-card__time'>
            <Clock size={14} weight='regular' />
            <span>
              {formatTimeLocale(servicefields?.STARTTIME, timeFormat)}
            </span>
          </div>
        )}

        <div className='activity-card__description-container'>
          {servicefields?.PROVIDERNAME && (
            <div>
              <p className='activity-card__description'>
                {servicefields?.PROVIDERNAME || ''}
              </p>
            </div>
          )}
          {servicefields?.CONFIRMATIONINFORMATION && (
            <div className='activity-card__confirmation'>
              {t('services:confirmationLabel', 'Confirmation')}:{' '}
              {servicefields?.CONFIRMATIONINFORMATION || ''}
            </div>
          )}
        </div>

        {isDesktop && (
          <>
            <div className='activity-card__desktop-preview'>
              {servicefields?.DURATION && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:duration', 'Duration')}
                  </p>
                  <div className='activity-card__modal-field__item'>
                    <Clock size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      {formatDurationDisplay(servicefields.DURATION)}
                    </p>
                  </div>
                </div>
              )}
              {servicefields?.ADDRESS && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:location', 'Location')}
                  </p>
                  <div className='activity-card__modal-field__item'>
                    <MapPin size={16} color='#667387' />
                    <a
                      className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                      role='button'
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMaps(servicefields.ADDRESS);
                      }}
                      title={t('services:openInMaps', 'Open in Maps')}
                    >
                      <span className='selectable-link'>
                        {servicefields?.ADDRESS || ''}
                      </span>
                    </a>
                  </div>
                </div>
              )}
              {servicefields?.PHONENUMBER && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:contact', ' Contact')}
                  </p>
                  <div className='activity-card__modal-field__item'>
                    <Phone size={16} color='#667387' />
                    <a
                      className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                      href={`tel:${servicefields.PHONENUMBER || ''}`}
                      onClick={(e) => e.stopPropagation()}
                      title={t('services:callPhone', 'Call Phone')}
                    >
                      <span className='selectable-link'>
                        {servicefields.PHONENUMBER || ''}
                      </span>
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className='activity-card__desktop-preview'>
              {servicefields?.LINK && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:website', 'Website')}
                  </p>
                  <div className='activity-card__modal-field__item'>
                    <ArrowSquareOut size={16} color='#667387' />
                    <a
                      className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                      href={servicefields.LINK || '#'}
                      target='_blank'
                      rel='noopener noreferrer'
                      title={t('services:visitWebsite', 'Visit Website')}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t('services:visitWebsite', 'Visit Website')}
                    </a>
                  </div>
                </div>
              )}
              {servicefields?.MEALSINCLUDED?.length > 0 && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:mealsIncluded', 'Meals Included')}
                  </p>

                  <div className='activity-card__modal-field__item'>
                    <ForkKnife size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      {servicefields?.MEALSINCLUDED?.join(', ') || ''}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {servicefields?.DESCRIPTION && (
              <div className='activity-card__modal-rich-text'>
                <Editor
                  id={`service-editor-${tripdetaileddayserviceid}-description`}
                  content={servicefields?.DESCRIPTION}
                  editable={false}
                  isPreviewing={true}
                />
              </div>
            )}
            {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
              <div className='activity-card__modal-field'>
                <p className='activity-card__modal-field__title'>
                  {t('services:attachedDocument', 'Attached Document')}
                </p>
                <div>
                  {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                    <a
                      key={idx}
                      href={attachment?.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='activity-card__attachment-link'
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <FileText size={20} />
                      <span>{attachment?.originalName}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  //Modal Content
  return (
    <div id={`service-card-activity-${tripdetaileddayserviceid}`}>
      <div>
        {servicefields?.CONFIRMATIONINFORMATION && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:confirmationLabel', 'Confirmation')}
            </p>
            <div className='activity-card__modal__confirmation'>
              {servicefields?.CONFIRMATIONINFORMATION || ''}
            </div>
          </div>
        )}

        {servicefields?.PROVIDERNAME && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:provider', 'Provider')}
            </p>
            <p className='activity-card__modal-field__value'>
              {servicefields?.PROVIDERNAME || ''}
            </p>
          </div>
        )}

        {servicefields?.DURATION && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:duration', 'Duration')}
            </p>
            <div className='activity-card__modal-field__item'>
              <Clock size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                {formatDurationDisplay(servicefields.DURATION)}
              </p>
            </div>
          </div>
        )}

        {servicefields?.ADDRESS && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:location', 'Location')}
            </p>
            <div className='activity-card__modal-field__item'>
              <MapPin size={16} color='#667387' />
              <a
                className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                role='button'
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openMaps(servicefields.ADDRESS);
                }}
                title={t('services:openInMaps', 'Open in Maps')}
              >
                <span className='selectable-link'>
                  {servicefields?.ADDRESS || ''}
                </span>
              </a>
            </div>
          </div>
        )}

        {servicefields?.PHONENUMBER && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:contact', ' Contact')}
            </p>
            <div className='activity-card__modal-field__item'>
              <Phone size={16} color='#667387' />
              <a
                className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                href={`tel:${servicefields.PHONENUMBER || ''}`}
                title={t('services:callPhone', 'Call Phone')}
              >
                <span className='selectable-link'>
                  {servicefields.PHONENUMBER || ''}
                </span>
              </a>
            </div>
          </div>
        )}

        {servicefields?.LINK && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:website', 'Website')}
            </p>
            <div className='activity-card__modal-field__item'>
              <ArrowSquareOut size={16} color='#667387' />
              <a
                className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                href={servicefields.LINK || '#'}
                target='_blank'
                rel='noopener noreferrer'
                title={t('services:visitWebsite', 'Visit Website')}
              >
                {t('services:visitWebsite', 'Visit Website')}
              </a>
            </div>
          </div>
        )}

        {servicefields?.MEALSINCLUDED?.length > 0 && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:mealsIncluded', 'Meals Included')}
            </p>

            <div className='activity-card__modal-field__item'>
              <ForkKnife size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                {servicefields?.MEALSINCLUDED?.join(', ') || ''}
              </p>
            </div>
          </div>
        )}

        {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:attachedDocument', 'Attached Document')}
            </p>
            <div>
              {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                <a
                  key={idx}
                  href={attachment?.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='activity-card__attachment-link'
                >
                  <FileText size={20} />
                  <span>{attachment?.originalName}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {servicefields?.DESCRIPTION && (
          <div className='activity-card__modal-rich-text'>
            <Editor
              id={`service-editor-${tripdetaileddayserviceid}-description`}
              content={servicefields?.DESCRIPTION}
              editable={false}
              isPreviewing={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

ActivityCard.propTypes = {
  servicefields: PropTypes.object,
  servicetype: PropTypes.object,
  tripdetaileddayserviceid: PropTypes.number,
  isPreview: PropTypes.bool,
  isDesktop: PropTypes.bool
};

export default ActivityCard;
